import { ClaimReservation, ClaimReservationWithTimeslot } from '@Generated/back-office-client/src'
import { useMutation } from '@tanstack/react-query'
import { useState } from 'react'
import { ClaimReservationsServiceAPI } from '@API/data-services/claim-reservations-service'

export const useVisitStatus = (
  reservation: ClaimReservationWithTimeslot,
  onReservationUpdated: (reservation: ClaimReservationWithTimeslot) => void,
  onError: (message: string) => void,
) => {
  const [checkedInOverride, setCheckedInOverride] = useState<boolean | undefined>(undefined)
  const [noShowOverride, setNoShowOverride] = useState<boolean | undefined>(undefined)

  const isCheckedIn = checkedInOverride ?? !!reservation.claimReservationDetails.checkedInAt
  const isNoShow = noShowOverride ?? !!reservation.claimReservationDetails.noShowAt

  const { mutate: toggleCheckedIn } = useMutation<ClaimReservation | void, Error, boolean>({
    mutationFn: (checked) => {
      if (checked) {
        return ClaimReservationsServiceAPI.setCheckedIn(reservation.id)
      }
      return ClaimReservationsServiceAPI.clearCheckedIn(reservation.id)
    },
    onMutate: (checked) => {
      setCheckedInOverride(checked)
      if (checked) {
        setNoShowOverride(false)
      }
    },
    onSuccess: (result, checked) => {
      setCheckedInOverride(undefined)
      setNoShowOverride(undefined)
      onReservationUpdated({
        ...reservation,
        claimReservationDetails: {
          ...reservation.claimReservationDetails,
          checkedInAt: checked ? ((result as ClaimReservation).checkedInAt ?? null) : null,
          noShowAt: checked ? null : reservation.claimReservationDetails.noShowAt,
        },
      })
    },
    onError: () => {
      setCheckedInOverride(undefined)
      setNoShowOverride(undefined)
      onError('Failed to update visit status')
    },
  })

  const { mutate: toggleNoShow } = useMutation<ClaimReservation | void, Error, boolean>({
    mutationFn: (checked) => {
      if (checked) {
        return ClaimReservationsServiceAPI.setNoShow(reservation.id)
      }
      return ClaimReservationsServiceAPI.clearNoShow(reservation.id)
    },
    onMutate: (checked) => {
      setNoShowOverride(checked)
      if (checked) {
        setCheckedInOverride(false)
      }
    },
    onSuccess: (result, checked) => {
      setCheckedInOverride(undefined)
      setNoShowOverride(undefined)
      onReservationUpdated({
        ...reservation,
        claimReservationDetails: {
          ...reservation.claimReservationDetails,
          noShowAt: checked ? ((result as ClaimReservation).noShowAt ?? null) : null,
          checkedInAt: checked ? null : reservation.claimReservationDetails.checkedInAt,
        },
      })
    },
    onError: () => {
      setCheckedInOverride(undefined)
      setNoShowOverride(undefined)
      onError('Failed to update visit status')
    },
  })

  return { isCheckedIn, isNoShow, toggleCheckedIn, toggleNoShow }
}
